import type { FC } from 'react';
import React, { useContext } from 'react';

import type { Resource } from 'i18next';
import i18next from 'i18next';
import { I18nextProvider } from 'react-i18next';

import type { CogI18nInstance } from '../../types/i18n';
import {
  getLanguage as defaultGetLanguage,
  selectLanguage as defaultSelectLanguage,
} from '../../utils';
import { I18nWrapperContext } from './I18nWrapper.context';
import { TranslationContextProvider } from './TranslationContext';

export type I18nWrapperProps = {
  children: React.ReactNode;
  errorScreen?: (e: Error) => React.ReactElement | null;
  loadingScreen?: React.ReactElement | null;
  translations: Resource;
  defaultNamespace?: string;
  /**
   * Pre-built Cog i18n instance. When provided, I18nWrapper skips creation
   * entirely and uses it as-is — the caller owns its lifecycle (init,
   * language, resources). Useful for tests/mocking.
   */
  i18n?: CogI18nInstance<Record<string, string>>;
  getLanguage?: typeof defaultGetLanguage;
  selectLanguage?: typeof defaultSelectLanguage;
};

const I18nWrapper: FC<I18nWrapperProps> = ({
  children,
  defaultNamespace,
  errorScreen,
  loadingScreen,
  translations,
  i18n,
  getLanguage = defaultGetLanguage,
  selectLanguage = defaultSelectLanguage,
}): JSX.Element => {
  const { useI18nWrapperViewModel } = useContext(I18nWrapperContext);
  const {
    isLoading,
    error,
    i18n: cogI18n,
  } = useI18nWrapperViewModel({
    i18n,
    translations,
    getLanguage,
    selectLanguage,
    defaultNamespace,
  });
  if (error) {
    return <>{errorScreen ? errorScreen(error) : error.message}</>;
  }

  if (isLoading || !cogI18n) {
    return <>{loadingScreen}</>;
  }

  // A caller-supplied Cog instance has no underlying raw i18next instance to
  // hand to <I18nextProvider> — only the shared/created default path does.
  if (i18n) {
    return (
      <TranslationContextProvider value={i18n}>
        {children}
      </TranslationContextProvider>
    );
  }

  return (
    <I18nextProvider i18n={i18next}>
      <TranslationContextProvider value={cogI18n}>
        {children}
      </TranslationContextProvider>
    </I18nextProvider>
  );
};

export default I18nWrapper;
